import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateOfferRequest,
  EditOfferRequest,
  GetAllCoinsDeductionsResponse,
  GetServiceLimitConfigsResponse,
  GetOffersParams,
  GetOffersResponse,
} from '../../types/coins';
import { baseQueryWithReauth } from './baseQuery';

export const coinsOffersServices = createApi({
  reducerPath: 'coinsOffersServices',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['COINS_PLANS', 'COINS_SERVICES', 'COINS_REWARDS'],
  endpoints: ({ query, mutation }) => ({
    getCoinsOffers: query<GetOffersResponse, GetOffersParams>({
      query: (params) => ({
        url: '/payment-service/v1/coins-plans/plans',
        method: 'GET',
        params: {
          page: (params.page ?? 1) - 1,
          limit: params.limit ?? 10,
          search: params.search || undefined,
        },
      }),
      providesTags: ['COINS_PLANS'],
    }),

    createCoinsOffer: mutation<{ payload: { id: string } }, CreateOfferRequest>(
      {
        query: (body) => ({
          url: '/payment-service/v1/coins-plans',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['COINS_PLANS'],
      }
    ),

    editCoinsOffer: mutation<
      { payload: string },
      { id: string; body: EditOfferRequest }
    >({
      query: ({ id, body }) => ({
        url: `/payment-service/v1/coins-plans/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['COINS_PLANS'],
    }),

    toggleCoinsOfferActive: mutation<
      { payload: string },
      { id: string; active: boolean }
    >({
      query: ({ id, active }) => ({
        url: `/payment-service/v1/coins-plans/${id}/active`,
        method: 'PUT',
        params: { active },
      }),
      invalidatesTags: ['COINS_PLANS'],
    }),

    deleteCoinsOffer: mutation<{ payload: string }, string>({
      query: (id) => ({
        url: `/payment-service/v1/coins-plans/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['COINS_PLANS'],
    }),

    getCoinsService: query<GetOffersResponse, GetOffersParams>({
      query: (params) => ({
        url: '/payment-service/v1/coins-deduction/by-profile-type',
        method: 'GET',
        params: {
          page: (params.page ?? 1) - 1,
          limit: params.limit ?? 10,
          search: params.search || undefined,
        },
      }),
      providesTags: ['COINS_SERVICES'],
    }),

    getAllCoinsDeductions: query<GetAllCoinsDeductionsResponse, void>({
      query: () => ({
        url: '/payment-service/v1/coins-deduction/all',
        method: 'GET',
      }),
      providesTags: ['COINS_SERVICES'],
    }),

    getServiceLimitConfigs: query<GetServiceLimitConfigsResponse, void>({
      query: () => ({
        url: '/payment-service/v1/service-limit-config/all',
        method: 'GET',
      }),
      providesTags: ['COINS_SERVICES'],
    }),

    updateCoinsService: mutation<
      { payload: string },
      { id: string; body: { coinsNo: number } }
    >({
      query: ({ id, body }) => ({
        url: `/payment-service/v1/coins-deduction/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['COINS_SERVICES'],
    }),

    updateServiceLimitConfig: mutation<
      { payload: string },
      { id: string; body: { limitNo: number } }
    >({
      query: ({ id, body }) => ({
        url: `/payment-service/v1/service-limit-config/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['COINS_SERVICES'],
    }),

    getCoinsRewards: query<GetOffersResponse, GetOffersParams>({
      query: (params) => ({
        url: '/payments-service/v1/coins-reward/by-profile-type',
        method: 'GET',
        params: {
          page: (params.page ?? 1) - 1,
          limit: params.limit ?? 10,
          search: params.search || undefined,
        },
      }),
      providesTags: ['COINS_REWARDS'],
    }),

    updateCoinsReward: mutation<
      { payload: string },
      { id: string; body: { coinsNo: number; duration?: number | null } }
    >({
      query: ({ id, body }) => ({
        url: `/payments-service/v1/coins-reward/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['COINS_REWARDS'],
    }),
  }),
});

export const {
  useGetCoinsOffersQuery,
  useCreateCoinsOfferMutation,
  useEditCoinsOfferMutation,
  useToggleCoinsOfferActiveMutation,
  useDeleteCoinsOfferMutation,
  useGetCoinsServiceQuery,
  useGetAllCoinsDeductionsQuery,
  useGetServiceLimitConfigsQuery,
  useUpdateCoinsServiceMutation,
  useUpdateServiceLimitConfigMutation,
  useGetCoinsRewardsQuery,
  useUpdateCoinsRewardMutation,
} = coinsOffersServices;
